import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CalendarClock, ClipboardList, Stethoscope } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { consultApi, consultError } from "../lib/consult";
import { startConsultPayment } from "../lib/cashfree";
import { toast } from "sonner";
import { formatINR } from "../lib/constants";
import { PageLoader } from "../components/LoadingRing";

const tone = (s) => ({
    scheduled: "text-gold border-gold/40", completed: "text-lime border-lime/40",
    cancelled: "text-red-400 border-red-400/40",
}[s] || "text-zinc-300 border-white/15");

export default function MyConsultations() {
    const { customer, loading } = useCustomerAuth();
    const [rows, setRows] = useState([]);
    const [paying, setPaying] = useState("");

    const pay = async (bookingId) => {
        setPaying(bookingId);
        try {
            sessionStorage.setItem("rhm_pending_consult", bookingId);
            await startConsultPayment(bookingId);
        } catch (e) {
            toast.error(consultError(e));
            setPaying("");
        }
    };

    useEffect(() => {
        if (!customer) return;
        consultApi.get("/me/bookings").then(({ data }) => setRows(data)).catch(() => setRows([]));
    }, [customer]);

    if (loading) return <PageLoader />;
    if (!customer) return <Navigate to="/account/login" replace />;

    return (
        <div className="pt-28 md:pt-32 pb-24" data-testid="my-consultations-page">
            <div className="mx-auto max-w-5xl px-6 md:px-12">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="eyebrow mb-3">My Consultations</div>
                        <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight">Your care history</h1>
                    </div>
                    <div className="flex gap-3">
                        <Link to="/account" className="btn-pill btn-secondary" data-testid="back-to-account">My Account</Link>
                        <Link to="/consultation/book" className="btn-pill btn-primary" data-testid="book-new-consultation">Book Consultation</Link>
                    </div>
                </div>

                {rows.length === 0 ? (
                    <div className="mt-12 border border-white/10 bg-bg1 p-10 text-center" data-testid="no-consultations">
                        <Stethoscope className="w-7 h-7 text-gold mx-auto" />
                        <div className="mt-4 font-display text-2xl uppercase tracking-tight">No consultations yet</div>
                        <p className="mt-2 text-sm text-zinc-400">Book a consultation and your records will appear here.</p>
                    </div>
                ) : (
                    <div className="mt-10 space-y-4" data-testid="consultation-list">
                        {rows.map((b) => (
                            <Link key={b.id} to={`/account/consultations/${b.id}`}
                                data-testid={`consultation-row-${b.consultation_id}`}
                                className="block border border-white/10 bg-bg1 p-6 hover:border-gold/40 transition-colors">
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <div className="mono text-[10px] tracking-[0.2em] text-gold">{b.consultation_id}</div>
                                        <div className="mt-2 font-display text-2xl uppercase tracking-tight">{b.package_name}</div>
                                        <div className="mt-2 text-sm text-zinc-400 flex flex-wrap gap-x-5 gap-y-1">
                                            <span className="inline-flex items-center gap-1.5">
                                                <Stethoscope className="w-3.5 h-3.5" /> {b.doctor_name}</span>
                                            <span className="inline-flex items-center gap-1.5">
                                                <CalendarClock className="w-3.5 h-3.5" /> {b.slot_label}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <span className={`mono text-[9px] tracking-[0.16em] px-2.5 py-1 border ${tone(b.status)}`}>
                                            {b.status.toUpperCase()}
                                        </span>
                                        <span className="text-sm text-zinc-300">{formatINR(b.amount)}</span>
                                        <span className="mono text-[9px] tracking-[0.14em] text-zinc-500">
                                            PAYMENT {(b.payment_status || "pending").toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                                {b.intake_status !== "submitted" && b.status === "scheduled" && (
                                    <div className="mt-4 inline-flex items-center gap-2 mono text-[10px] tracking-[0.16em] text-lime">
                                        <ClipboardList className="w-3.5 h-3.5" /> INTAKE PENDING — CONTINUE
                                    </div>
                                )}
                                {["pending", "failed"].includes(b.payment_status) && Number(b.amount || 0) > 0
                                    && b.status !== "cancelled" && (
                                    <button
                                        data-testid={`consultation-pay-${b.consultation_id}`}
                                        disabled={paying === b.id}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); pay(b.id); }}
                                        className="mt-4 ml-0 sm:ml-5 btn-pill btn-primary text-[10px] disabled:opacity-50">
                                        {paying === b.id ? "OPENING…" : `PAY ${formatINR(b.amount)}`}
                                    </button>
                                )}
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
